// FR-RD9: a missing OS grant must read as a permission problem, not a broken recorder.
// The how-to-grant strings come from the Tauri probe so the copy matches the host OS.

import type { DesktopCapabilities } from "../desktop";

const SAFARI_RE = /^((?!chrome|chromium|crios|android).)*safari/i;

/** Safari has no `getDisplayMedia` audio track at all (DS readme / decision #1), so the mode
 * picker's system-audio row says so up front rather than waiting for a failed capture to find
 * out. Reads `navigator` only, the same pattern `@grackle/ui`'s hotkeys.ts uses, so a page
 * without one (SSR, Node tests) just gets the non-Safari answer. */
export function supportsSystemAudioCapture(
  nav:
    | { userAgent?: string; mediaDevices?: { getDisplayMedia?: unknown } }
    | null
    | undefined = typeof navigator === "undefined" ? null : navigator,
): boolean {
  // Check the navigator we were given (not the global one), so callers and tests that pass a
  // user agent are not overridden by the host environment (jsdom has no getDisplayMedia).
  if (nav && "mediaDevices" in nav && !nav.mediaDevices?.getDisplayMedia) return false;
  const ua = nav?.userAgent || "";
  return !SAFARI_RE.test(ua);
}

export function isPermissionDenied(err: unknown): boolean {
  if (err && typeof err === "object") {
    const name = "name" in err ? String((err as { name: unknown }).name) : "";
    if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
      return true;
    }
  }
  const msg = err instanceof Error ? err.message : String(err ?? "");
  return /permission|not allowed|denied|not authorized|cannot open display|screen recording/i.test(
    msg,
  );
}

export function permissionHint(
  caps: DesktopCapabilities | null | undefined,
  id: "microphone" | "camera" | "screen",
): { label: string; how_to_grant: string } | null {
  const live = caps?.permission_states?.find((p) => p.id === id);
  if (live) return { label: live.label, how_to_grant: live.how_to_grant };
  const staticHint = caps?.permissions?.find((p) => p.id === id);
  if (staticHint) return { label: staticHint.label, how_to_grant: staticHint.how_to_grant };
  return null;
}

/** Human copy for a getUserMedia / getDisplayMedia / native-start failure. */
export function explainCaptureError(
  err: unknown,
  caps: DesktopCapabilities | null | undefined,
  kind: "microphone" | "camera" | "screen" | "mixed" = "mixed",
): string {
  const raw = err instanceof Error ? err.message : String(err ?? "Couldn't start capture.");
  if (isPermissionDenied(err)) {
    const hint = permissionHint(caps, kind === "mixed" ? "screen" : kind);
    const grant = hint?.how_to_grant;
    const label = hint?.label ?? (kind === "mixed" ? "camera, microphone, or screen" : kind);
    return grant
      ? `Grackle is not allowed to use the ${label.toLowerCase()}. ${grant} The recorder itself is fine — the OS blocked this source.`
      : `Grackle is not allowed to use the ${label.toLowerCase()}. Check the OS privacy settings for this app. The recorder itself is fine — the OS blocked this source.`;
  }
  const name = err && typeof err === "object" && "name" in err ? String((err as { name: unknown }).name) : "";
  if (name === "NotFoundError" || /not found|no device/i.test(raw)) {
    return "No camera or microphone is available on this machine.";
  }
  if (name === "NotReadableError" || /in use|busy/i.test(raw)) {
    return "The camera or microphone is already in use by another application.";
  }
  return raw || "Couldn't access the camera, microphone, or screen.";
}

export function deniedPermissionNotices(
  caps: DesktopCapabilities | null | undefined,
): { id: string; text: string }[] {
  if (!caps?.permission_states) return [];
  return caps.permission_states
    .filter((p) => p.state === "denied")
    .map((p) => ({
      id: p.id,
      text: `${p.label} is blocked. ${p.how_to_grant}${
        p.blocked_reason ? ` ${p.blocked_reason}` : ""
      }`,
    }));
}
